import React, { useState, useRef } from 'react';
import {
  Avatar,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import avatarService from '../../services/avatarService';

const AvatarUpload = ({
  currentAvatar,
  userName,
  size = 80,
  onAvatarChange,
  editable = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  const handleClick = (event) => {
    if (editable) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('Chỉ chấp nhận file ảnh');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File quá lớn. Tối đa 5MB');
      return;
    }

    setLoading(true);
    handleClose();

    try {
      const result = await avatarService.upload(file);
      if (result.success && onAvatarChange) {
        onAvatarChange(result.avatar);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Lỗi khi upload ảnh');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    handleClose();
    setLoading(true);

    try {
      const result = await avatarService.delete();
      if (result.success && onAvatarChange) {
        onAvatarChange(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Lỗi khi xóa ảnh');
    } finally {
      setLoading(false);
    }
  };

  const initials = avatarService.getInitials(userName);
  const bgColor = avatarService.getColor(userName);

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        style={{ display: 'none' }}
      />

      <Avatar
        src={currentAvatar}
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.4,
          bgcolor: currentAvatar ? 'transparent' : bgColor,
          cursor: editable ? 'pointer' : 'default',
          transition: 'all 0.2s',
          '&:hover': editable ? {
            opacity: 0.8,
            transform: 'scale(1.02)',
          } : {},
        }}
        onClick={handleClick}
      >
        {loading ? (
          <CircularProgress size={size * 0.4} sx={{ color: 'white' }} />
        ) : (
          initials
        )}
      </Avatar>

      {editable && (
        <Tooltip title="Đổi ảnh">
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              bgcolor: 'primary.main',
              color: 'white',
              width: size * 0.35,
              height: size * 0.35,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
            onClick={handleClick}
          >
            <CameraIcon sx={{ fontSize: size * 0.18 }} />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => fileInputRef.current?.click()}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chọn ảnh mới</ListItemText>
        </MenuItem>
        {currentAvatar && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Xóa ảnh</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default AvatarUpload;
